import { recordPerformance } from '../observability/performance';
import { statsOptOutHeaders } from '../services/statsOptOut';

export type CacheResource = 'dashboard-page' | 'dashboard-summary' | 'record' | 'search';
export interface CacheRequest {
  resource: CacheResource;
  key: string;
  url: string;
}
export interface CacheSnapshot<T> {
  data?: T;
  error?: Error;
  validating: boolean;
  invalidated: boolean;
  invalidationVersion: number;
}
interface Entry extends CacheSnapshot<unknown> {
  request: CacheRequest;
  updatedAt: number;
  accessedAt: number;
  restored?: boolean;
  etag?: string;
}
const POLICIES = {
  'dashboard-page': { fresh: 60_000, retain: 300_000, limit: 10, persist: true },
  'dashboard-summary': { fresh: 60_000, retain: 300_000, limit: 1, persist: true },
  record: { fresh: 120_000, retain: 600_000, limit: 20, persist: false },
  search: { fresh: 60_000, retain: 300_000, limit: 20, persist: false },
};
const STORAGE_KEY = 'vedra.server-cache.v1';
const MAX_STORAGE_BYTES = 2 * 1024 * 1024;
const EMPTY: CacheSnapshot<never> = { validating: false, invalidated: false, invalidationVersion: 0 };
export class ApiReadError extends Error {
  constructor(public status: number) { super(status === 403 ? 'You no longer have access to this information.' : 'Unable to load this information. Please try again.'); }
}

/** Cache lifetimes belong to the authenticated app, not an individual route. */
export class ServerCache {
  private entries = new Map<string, Entry>();
  private pending = new Map<string, { controller: AbortController; promise: Promise<unknown> }>();
  private listeners = new Set<() => void>();
  private scope: string | null = null;
  private revision = 0;
  constructor(private storage: () => Storage | undefined = () => window.sessionStorage) {}

  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getRevision = () => this.revision;
  getScope = () => this.scope;
  private emit() { this.revision += 1; this.listeners.forEach(listener => listener()); }
  private key(request: CacheRequest) { return JSON.stringify([this.scope, request.resource, request.key]); }
  private removeStorage() { try { this.storage()?.removeItem(STORAGE_KEY); } catch { /* Memory remains usable. */ } }

  setScope(scope: string | null) {
    if (scope === this.scope) return;
    const firstAuthentication = this.scope === null && scope !== null && scope !== 'anonymous' && !scope.startsWith('volatile:');
    this.pending.forEach(value => value.controller.abort());
    this.pending.clear();
    this.entries.clear();
    if (!firstAuthentication) this.removeStorage();
    this.scope = scope;
    if (firstAuthentication) this.restore();
    this.emit();
  }

  clear() {
    this.pending.forEach(value => value.controller.abort());
    this.pending.clear();
    this.entries.clear();
    this.removeStorage();
    this.emit();
  }

  read<T>(request: CacheRequest): CacheSnapshot<T> {
    const entry = this.entries.get(this.key(request));
    if (!entry || !this.scope) return EMPTY;
    const expired = Date.now() - entry.updatedAt >= POLICIES[request.resource].retain;
    return { ...entry, data: expired ? undefined : entry.data as T };
  }

  async fetch<T>(request: CacheRequest, force = false): Promise<T> {
    if (!this.scope) throw new Error('Authentication has not resolved.');
    const key = this.key(request);
    const pending = this.pending.get(key);
    if (pending) return pending.promise as Promise<T>;
    const previous = this.entries.get(key);
    const policy = POLICIES[request.resource];
    const age = previous ? Date.now() - previous.updatedAt : Infinity;
    if (!force && previous?.data !== undefined && !previous.invalidated && !previous.restored && age < policy.fresh) {
      previous.accessedAt = Date.now();
      this.entries.delete(key);
      this.entries.set(key, previous);
      recordPerformance('cache-hit', request.resource);
      return previous.data as T;
    }
    const usable = previous && age < policy.retain ? previous : undefined;
    const controller = new AbortController();
    const start = Date.now();
    const entry: Entry = {
      ...EMPTY, ...previous, request, data: usable?.data,
      updatedAt: previous?.updatedAt || 0, accessedAt: start,
      validating: true, error: undefined,
    };
    this.entries.set(key, entry);
    const isCurrent = () => this.pending.get(key)?.controller === controller;
    // Defer execution so the in-flight entry exists even with immediate failures.
    const promise = Promise.resolve().then(async () => {
      try {
        const headers: Record<string, string> = {};
        if (request.resource === 'record' && usable?.etag) headers['If-None-Match'] = usable.etag;
        // Invenio raises a view event on every read of a record. The first read
        // in a session is the view; the revalidations that follow are the same
        // person still on the page, so they opt out of being counted. An entry
        // restored from storage is someone coming back, which counts again.
        if (request.resource === 'record' && previous?.data !== undefined && !previous.restored) {
          Object.assign(headers, statsOptOutHeaders());
        }
        const response = await fetch(request.url, { credentials: 'include', cache: 'no-store', headers, signal: controller.signal });
        if (!isCurrent()) throw new DOMException('Superseded request', 'AbortError');
        if (response.status !== 304 && !response.ok) throw new ApiReadError(response.status);
        const data = response.status === 304 && usable?.data !== undefined ? usable.data : await response.json();
        if (!isCurrent()) throw new DOMException('Superseded request', 'AbortError');
        this.entries.set(key, {
          ...entry, data, updatedAt: Date.now(), accessedAt: Date.now(),
          validating: false, invalidated: false, restored: false,
          etag: response.headers.get('ETag') || usable?.etag,
        });
        this.prune(request.resource);
        this.persist();
        return data as T;
      } catch (error) {
        if (isCurrent()) {
          const denied = error instanceof ApiReadError && [401, 403, 404].includes(error.status);
          this.entries.set(key, { ...entry, data: denied ? undefined : entry.data, validating: false, error: error as Error });
          if (denied) this.persist();
          if (entry.data !== undefined) recordPerformance('refresh-error', request.resource, Date.now() - start);
          if (error instanceof ApiReadError && error.status === 401) {
            this.clear();
            window.dispatchEvent(new Event('vedra:auth-invalid'));
          }
        }
        throw error;
      } finally {
        if (isCurrent()) {
          this.pending.delete(key);
          recordPerformance('request', request.resource, Date.now() - start);
          this.emit();
        }
      }
    });
    this.pending.set(key, { controller, promise });
    this.prune(request.resource);
    this.emit();
    return promise;
  }

  invalidateRecords(recordId?: string) {
    this.entries.forEach((entry, key) => {
      if (entry.request.resource === 'record' && recordId && entry.request.key !== recordId) return;
      this.pending.get(key)?.controller.abort();
      this.pending.delete(key);
      this.entries.set(key, { ...entry, validating: false, invalidated: true, invalidationVersion: entry.invalidationVersion + 1 });
    });
    // Persisted pages must never survive a mutation without revalidation.
    this.persist();
    this.emit();
  }

  private prune(resource: CacheResource) {
    const entries = Array.from(this.entries.entries()).reverse().filter(([, e]) => e.request.resource === resource)
      .sort((a, b) => b[1].accessedAt - a[1].accessedAt);
    entries.slice(POLICIES[resource].limit).forEach(([key]) => {
      this.pending.get(key)?.controller.abort();
      this.pending.delete(key);
      this.entries.delete(key);
    });
  }

  private persist() {
    if (!this.scope || this.scope === 'anonymous' || this.scope.startsWith('volatile:')) return;
    let entries = Array.from(this.entries.values()).filter(entry =>
      POLICIES[entry.request.resource].persist && entry.data !== undefined && !entry.invalidated &&
      Date.now() - entry.updatedAt < POLICIES[entry.request.resource].retain
    ).sort((a, b) => b.accessedAt - a.accessedAt);
    try {
      // Three bytes per UTF-16 code unit is a conservative UTF-8 upper bound.
      let serialized = JSON.stringify({ scope: this.scope, entries });
      while (serialized.length * 3 > MAX_STORAGE_BYTES && entries.length) {
        entries = entries.slice(0, -1);
        serialized = JSON.stringify({ scope: this.scope, entries });
      }
      this.storage()?.setItem(STORAGE_KEY, serialized);
    } catch { this.removeStorage(); }
  }

  private restore() {
    try {
      const raw = this.storage()?.getItem(STORAGE_KEY);
      if (!raw || raw.length * 3 > MAX_STORAGE_BYTES) { this.removeStorage(); return; }
      const stored = JSON.parse(raw);
      if (stored.scope !== this.scope || !Array.isArray(stored.entries)) { this.removeStorage(); return; }
      for (const entry of stored.entries as Entry[]) {
        const resource = entry.request?.resource;
        if (!['dashboard-page', 'dashboard-summary'].includes(resource) || typeof entry.request?.key !== 'string' ||
          typeof entry.updatedAt !== 'number' || entry.updatedAt > Date.now() ||
          Date.now() - entry.updatedAt >= POLICIES[resource].retain || entry.data === undefined) continue;
        this.entries.set(this.key(entry.request), { ...entry, validating: false, error: undefined, restored: true, invalidationVersion: 0 });
        this.prune(resource);
      }
    } catch { this.removeStorage(); }
  }
}

export const serverCache = new ServerCache();
export const invalidateRecordData = (recordId?: string) => serverCache.invalidateRecords(recordId);
